/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { BasicCard, ShowMoreGridSection } from 'bundles/cds-labs/components';
import ModuleLayout, { noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmLink from 'bundles/cds-labs/components/Modules/components/PreservedUtmLink';
import { getColorFromColorName, isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';

import _t from 'i18n!nls/cds-labs';

const CARD_SIZE = { SMALL: 'small', REGULAR: 'regular', LARGE: 'large' } as const;

export type CardSize = (typeof CARD_SIZE)[keyof typeof CARD_SIZE];
export type ColorChoice = 'blue' | 'white' | 'black' | 'light-blue';

// The offset is half of the width of the card
const BACKGROUND_IMAGE_LEFT_OFFSET = 335;

export type CannedCollectionModuleCard = {
  id: string;
  headline?: string;
  body?: string;
  target?: string;
  ctaText?: string;
  cardBackgroundImage?: {
    width?: number;
    height?: number;
    url?: string;
    title?: string;
    description?: string;
  };
  onClick?: () => void;
};

type Props = {
  heading?: string;
  subheading?: string;
  cards: CannedCollectionModuleCard[];
  size: CardSize;
  color: ColorChoice;
  preloadImage?: boolean;
  lazyLoadImage?: boolean;
  showAllEntries?: boolean;
} & Pick<ModuleLayoutConfig, 'backgroundColor' | 'removeTopPadding' | 'removeBottomPadding'>;

const styles = {
  card: css`
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    text-decoration: none;
    overflow: hidden;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }
  `,

  cardSizeLarge: css`
    min-height: 266px;
    padding: var(--cds-spacing-800) var(--cds-spacing-300) var(--cds-spacing-400) var(--cds-spacing-400);
  `,

  cardSizeRegular: css`
    min-height: 192px;
    padding: var(--cds-spacing-300);
  `,

  cardSizeSmall: css`
    min-height: 150px;
    padding: var(--cds-spacing-300);
  `,

  cardColorBlack: css`
    background-color: var(--cds-color-neutral-primary);
    border: none;
  `,

  cardColorBlue: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    border: none;
  `,

  cardColorWhite: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-disabled-strong);
  `,

  cardTitle: css`
    margin-bottom: var(--cds-spacing-100);
  `,

  cta: css`
    display: flex;
    align-items: center;
    padding: var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-100) 0;
  `,
  ctaText: css`
    && {
      margin-right: var(--cds-spacing-100);
    }
  `,
  cardContentWrapper: css`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  cardContent: (setMaxWidth: boolean) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: ${setMaxWidth ? `${BACKGROUND_IMAGE_LEFT_OFFSET}px` : 'unset'};
    box-sizing: border-box;
  `,
  backgroundImage: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `,
  backgroundImageInner: (offset: number, isRtl: boolean) => css`
    position: absolute;
    bottom: 0;
    left: ${offset}px;

    img {
      ${isRtl ? `transform: scaleX(-1);` : ''};
    }
  `,

  // cancel the y-padding since the block already provides y-padding
  showMoreGridSection: css`
    padding-top: var(--cds-spacing-100);
    padding-bottom: var(--cds-spacing-100);
  `,

  heading: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  subheading: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

export function CannedCollectionModule(props: Props) {
  const { heading, subheading, cards, size, color, preloadImage, lazyLoadImage, showAllEntries } = props;
  const isRtl: boolean = isRightToLeft(_t.getLocale());

  let cardSizeStyle = styles.cardSizeRegular;
  let cardTitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'titleSmall';
  let cardSubtitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'bodySecondary';

  switch (size) {
    case CARD_SIZE.LARGE:
      cardSizeStyle = styles.cardSizeLarge;
      cardSubtitleVariant = 'bodyPrimary';
      break;
    case CARD_SIZE.REGULAR:
      cardSizeStyle = styles.cardSizeRegular;
      break;
    case CARD_SIZE.SMALL:
    default:
      cardSizeStyle = styles.cardSizeSmall;
      cardTitleVariant = 'subtitleLarge';
      break;
  }

  const cardColorStyle = `background-color:
    ${getColorFromColorName(color)};
  `;

  const cardBorderStyle =
    color === 'white' ? `border: 1px solid var(--cds-color-neutral-disabled-strong);` : `border: none;`;

  const cardStyles = [styles.card, cardColorStyle, cardBorderStyle];

  const renderCardContent = (item: CannedCollectionModuleCard, isDarkBackground: boolean) => {
    const backgroundImage = item.cardBackgroundImage;

    const contents = (
      <div css={styles.cardContentWrapper}>
        {backgroundImage?.url && (
          <div css={styles.backgroundImage}>
            <div
              css={styles.backgroundImageInner(
                backgroundImage?.width
                  ? Math.max(BACKGROUND_IMAGE_LEFT_OFFSET * 2 - backgroundImage?.width, BACKGROUND_IMAGE_LEFT_OFFSET)
                  : BACKGROUND_IMAGE_LEFT_OFFSET,
                isRtl
              )}
            >
              <Image
                {...backgroundImage}
                style={{
                  maxWidth: 'unset',
                }}
                shouldPreload={preloadImage}
                shouldLazyLoad={lazyLoadImage}
              />
            </div>
          </div>
        )}
        <div css={[styles.cardContent(!!backgroundImage?.url), cardSizeStyle]}>
          <div>
            {item.headline && (
              <Typography2
                component="h3"
                variant={cardTitleVariant}
                css={styles.cardTitle}
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.headline}
              </Typography2>
            )}
            {item.body && (
              <Typography2
                variant={cardSubtitleVariant}
                component="div"
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.body}
              </Typography2>
            )}
          </div>
          {item.ctaText && (
            <div css={styles.cta}>
              <Typography2
                component="div"
                variant="actionPrimary"
                color={isDarkBackground ? 'invertBody' : 'body'}
                css={styles.ctaText}
              >
                {item.ctaText}
              </Typography2>
              <ArrowNextIcon color={isDarkBackground ? 'invert' : 'default'} />
            </div>
          )}
        </div>
      </div>
    );

    if (item.target) {
      const handleClick = () => {
        if (item.onClick) {
          item.onClick();
        }
      };
      return (
        <BasicCard
          component={PreservedUtmLink}
          href={item.target}
          openInNewTab={true}
          css={cardStyles}
          role="button"
          onClick={handleClick}
          trackingName="canned_collection_card"
        >
          {contents}
        </BasicCard>
      );
    } else {
      return <div css={cardStyles}>{contents}</div>;
    }
  };

  const isDarkCardBackground = color ? isInvertedColor(color) : false;
  let numDisplayedRows = size === CARD_SIZE.SMALL ? 2 : 1;
  if (showAllEntries) {
    numDisplayedRows = cards.length;
  }

  const contentPanel = () => (
    <div>
      {heading && (
        <Typography2 variant="titleSmall" component="h2" color="inherit" css={styles.heading}>
          {heading}
        </Typography2>
      )}
      {subheading && (
        <Typography2 variant="subtitleMedium" component="h3" color="inherit" css={styles.subheading}>
          {subheading}
        </Typography2>
      )}
      <ShowMoreGridSection
        items={cards}
        spacing={size === CARD_SIZE.LARGE ? 24 : 16}
        itemsGridConfig={size === CARD_SIZE.LARGE ? { xs: 12, sm: 6, md: 6 } : { xs: 12, sm: 6, md: 4 }}
        defaultRows={numDisplayedRows}
        renderItem={({ item }) => renderCardContent(item, isDarkCardBackground)}
        css={styles.showMoreGridSection}
      />
    </div>
  );

  return <ModuleLayout assembly={noImage(props)} renderContent={contentPanel} />;
}

export default CannedCollectionModule;
